import React, { useState } from 'react';
import {
  Trash2,
  Edit,
  Download,
  FolderIcon,
  FileIcon,
  PlusIcon,
  Check,
  X,
  SquareMinus,
  FolderOpen,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../../../../components/ui/input';

interface FileType {
  id: number;
  name: string;
  dateCreated: string;
  viewers: string[];
}

interface FolderType {
  id: string;
  name: string;
  count: number;
  files: FileType[];
}

interface EditingFolderType {
  id: string;
  name: string;
}

interface SelectedItem {
  id: string | number;
  type: 'folder' | 'file';
  name: string;
}

interface MyFilesTableProps {
  folders: FolderType[];
  expandedFolders: { [key: string]: boolean };
  toggleFolder: (folderName: string) => void;
  handleDeleteFolder: (folderId: number) => void;
  handleDeleteBlog: (blogId: number) => void;
  handleDownloadFile: (blogId: number, fileName: string) => void;
  onSchedule: (blogId: number) => void;
  handleUpdateFolder?: (folderId: string, newName: string) => void;
  onSelectionChange?: (selectedItems: SelectedItem[]) => void;
}

const MyFilesTable: React.FC<MyFilesTableProps> = ({
  folders,
  expandedFolders,
  toggleFolder,
  handleDeleteFolder,
  handleDeleteBlog,
  handleDownloadFile,
  handleUpdateFolder,
  onSelectionChange,
}) => {
  const navigate = useNavigate();
  const [editingFolder, setEditingFolder] = useState<EditingFolderType | null>(
    null
  );
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  // If no folders exist, show empty state
  if (folders.length === 0) {
    return (
      <div className="border border-gray-200 bg-background rounded-lg p-8">
        <div className="text-center flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <FileIcon size={24} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">
            No blogs created
          </h3>
          <p className="text-gray-500">
            Get started by creating your first blog
          </p>
        </div>
      </div>
    );
  }

  const handleEditClick = (e: React.MouseEvent, folder: FolderType) => {
    e.stopPropagation();
    setEditingFolder({ id: folder.id, name: folder.name });
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFolder(null);
  };

  const handleSaveEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editingFolder && handleUpdateFolder) {
      handleUpdateFolder(editingFolder.id, editingFolder.name);
      setEditingFolder(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && editingFolder && handleUpdateFolder) {
      handleUpdateFolder(editingFolder.id, editingFolder.name);
      setEditingFolder(null);
    } else if (e.key === 'Escape') {
      setEditingFolder(null);
    }
  };

  const toggleItemSelection = (item: SelectedItem, e: React.MouseEvent) => {
    e.stopPropagation();

    const isSelected = selectedItems.some(
      (selectedItem) =>
        selectedItem.id === item.id && selectedItem.type === item.type
    );

    let newSelectedItems: SelectedItem[];

    if (isSelected) {
      newSelectedItems = selectedItems.filter(
        (selectedItem) =>
          !(selectedItem.id === item.id && selectedItem.type === item.type)
      );
    } else {
      newSelectedItems = [...selectedItems, item];
    }

    setSelectedItems(newSelectedItems);

    if (onSelectionChange) {
      onSelectionChange(newSelectedItems);
    }
  };

  const isItemSelected = (id: string | number, type: 'folder' | 'file') => {
    return selectedItems.some((item) => item.id === id && item.type === type);
  };

  return (
    <div className="border border-gray-200 bg-background rounded-lg overflow-x-auto">
      <table className="w-full table-fixed min-w-[640px]">
        <thead>
          <tr className="bg-background-card text-primary border-b">
            <th className="text-left p-4 font-medium md:w-[30%] w-[40%]">
              Blog Name
            </th>
            <th className="text-left p-4 font-medium md:w-[25%] w-[30%] hidden md:table-cell">
              Date Created <span className="inline-block ml-1">↕</span>
            </th>
            <th className="text-left p-4 font-medium w-[20%] hidden md:table-cell">
              Viewers
            </th>
            <th className="p-4 text-left font-medium md:w-[25%] w-[30%]">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {folders.map((folder) => (
            <React.Fragment key={folder.id}>
              {/* Folder Row */}
              <tr
                className="border-b  cursor-pointer text-text-primary"
                onClick={() => toggleFolder(folder.name)}
              >
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <div className="">
                      {expandedFolders[folder.name] ? (
                        <div className="flex gap-2 items-center justify-center text-primary">
                          <SquareMinus size={18} />
                          <FolderOpen size={18} />
                        </div>
                      ) : (
                        <div className="flex gap-2 items-center justify-center text-primary">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={isItemSelected(folder.id, 'folder')}
                            onChange={() => {}}
                            onClick={(e) =>
                              toggleItemSelection(
                                {
                                  id: folder.id,
                                  type: 'folder',
                                  name: folder.name,
                                },
                                e
                              )
                            }
                          />
                          <FolderIcon size={18} className="" />
                        </div>
                      )}
                    </div>

                    {editingFolder?.id === folder.id ? (
                      <div
                        className="flex items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Input
                          value={editingFolder.name}
                          onChange={(e) =>
                            setEditingFolder({
                              ...editingFolder,
                              name: e.target.value,
                            })
                          }
                          onKeyDown={handleKeyDown}
                          autoFocus
                          className="h-7 py-0 text-sm"
                        />
                        <button
                          className="p-1 text-green-500 hover:text-green-700"
                          onClick={handleSaveEdit}
                        >
                          <Check size={16} />
                        </button>
                        <button
                          className="p-1 text-red-500 hover:text-red-700"
                          onClick={handleCancelEdit}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium">{folder.name}</span>
                    )}
                  </div>
                </td>
                <td className="p-4 ">
                  {folder.files[0]?.dateCreated || 'Wed, 27 Nov, 24'}
                </td>
                <td className="p-4"></td>
                <td className="p-4">
                  <div className="flex justify-start gap-1">
                    {!editingFolder && (
                      <>
                        <button
                          className="p-2 text-primary cursor-pointer"
                          onClick={(e) => handleEditClick(e, folder)}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          className="p-2 text-primary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(parseInt(folder.id));
                          }}
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>

              {/* Empty folder message */}
              {expandedFolders[folder.name] && folder.files.length === 0 && (
                <tr className="border-b">
                  <td colSpan={4} className="p-4 pl-12 text-gray-500 italic">
                    Your created blogs will be saved here
                  </td>
                </tr>
              )}

              {/* Files within folder */}
              {expandedFolders[folder.name] &&
                folder.files.map((file) => (
                  <tr
                    key={file.id}
                    className="border-b text-text-primary cursor-pointer"
                    onClick={() => {
                      navigate(`/ai-blog-writer/blog?id=${file.id}`);
                    }}
                  >
                    <td className="p-4 pl-12">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={isItemSelected(file.id, 'file')}
                          onChange={() => {}}
                          onClick={(e) =>
                            toggleItemSelection(
                              {
                                id: file.id,
                                type: 'file',
                                name: file.name,
                              },
                              e
                            )
                          }
                        />
                        <FileIcon size={18} className="text-gray-400" />
                        <span>{file.name}</span>
                      </div>
                    </td>
                    <td className="p-4">{file.dateCreated}</td>
                    <td className="p-4">
                      <div className="flex -space-x-2">
                        <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center text-white">
                          A
                        </div>
                        <div className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center text-white">
                          W
                        </div>
                        <div className="w-8 h-8 rounded-full bg-pink-400 flex items-center justify-center text-white">
                          B
                        </div>
                        <div className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center">
                          <PlusIcon size={14} />
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-start gap-1">
                        <button
                          className="p-2 text-primary cursor-pointer"
                          onClick={() =>
                            navigate(`/ai-blog-writer/blog?id=${file.id}`)
                          }
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          className="p-2 text-primary cursor-pointer"
                          onClick={() => handleDeleteBlog(file.id)}
                        >
                          <Trash2 size={18} />
                        </button>
                        <button
                          className="p-2 text-primary cursor-pointer"
                          onClick={() => handleDownloadFile(file.id, file.name)}
                          title="Export"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          className="p-2 text-primary cursor-pointer"
                          onClick={() => {
                            // Open in new tab or maximize
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <polyline points="9 21 3 21 3 15"></polyline>
                            <line x1="21" y1="3" x2="14" y2="10"></line>
                            <line x1="3" y1="21" x2="10" y2="14"></line>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MyFilesTable;
